/**
 * Technician mobile view. Optimised for touch.
 *
 * Answers per ADR — Technician Experience:
 *   • What do I need to fix?  (title, category, description)
 *   • Where is it?             (room + location note)
 *   • How urgent is it?        (priority badge)
 *   • Does it affect a guest?  (GUEST IMPACT badge)
 *   • When is it due?          (SLA countdown)
 */
import { useEffect, useState, useCallback } from "react";
import { Wrench, PlayCircle, PauseCircle, CheckCheck, MapPin, Clock, User } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, Badge } from "@/components/primitives";
import { Button } from "@/components/ui/button";

const PRIORITY_TONE = {
  LOW: "neutral", NORMAL: "info", HIGH: "warning",
  URGENT: "error", EMERGENCY: "error",
};
const STATE_LABEL = {
  assigned: "Assigned", accepted: "Accepted",
  in_progress: "In Progress", paused: "Paused",
  pending_inspection: "Waiting Inspection",
};

export default function MyWorkOrders() {
  const [wos, setWos] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/engineering/my-work-orders");
      setWos(data);
    } catch (e) { toast.error(formatApiError(e) || "Failed"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const act = async (id, path, body) => {
    try {
      await api.post(`/engineering/work-orders/${id}${path}`, body || {});
      refresh();
    } catch (e) { toast.error(formatApiError(e) || "Failed"); }
  };

  return (
    <div className="space-y-4" data-testid="my-work-orders-page">
      <PageHeader title="My Work Orders" subtitle="Your assigned repairs — fix them and restore rooms to service."/>
      {loading && <p className="text-hocp-textTertiary italic text-sm">Loading...</p>}
      {!loading && wos.length === 0 && (
        <p className="text-hocp-textTertiary italic text-sm" data-testid="my-empty">
          You have no assigned work orders. Nice work.
        </p>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {wos.map(w => (
          <div key={w.id}
                className={`hocp-card p-4 ${w.guest_impact ? "border-hocp-error/40" : ""}`}
                data-testid={`my-wo-${w.id}`}>
            <div className="flex items-start justify-between mb-2">
              <div>
                <div className="font-mono text-[11px] text-hocp-textTertiary">{w.code}</div>
                <div className="font-display font-semibold text-[16px] leading-tight">{w.title}</div>
              </div>
              <div className="flex flex-col items-end gap-1">
                <Badge tone={PRIORITY_TONE[w.priority_code]} size="sm">{w.priority_code}</Badge>
                {w.guest_impact && <Badge tone="error" size="sm">GUEST</Badge>}
              </div>
            </div>
            <div className="text-[12px] text-hocp-textSecondary space-y-1 mb-3">
              <div className="flex items-center gap-1.5">
                <MapPin className="w-3 h-3"/>
                {w.location_type === "room"
                  ? `Room ${w.room_number || "—"}`
                  : (w.location_note || w.location_type)}
              </div>
              <div className="flex items-center gap-1.5">
                <Wrench className="w-3 h-3"/> {w.category_code}
              </div>
              <div className="flex items-center gap-1.5">
                <Clock className="w-3 h-3"/> SLA:
                <SlaChip sla={w.sla_target_at}/>
              </div>
            </div>
            {w.description && (
              <p className="text-[12px] text-hocp-textSecondary mb-3">{w.description}</p>
            )}
            <div className="flex gap-2 flex-wrap">
              {w.state === "assigned" && (
                <Button size="sm" onClick={() => act(w.id, "/accept")} data-testid={`accept-${w.id}`}>Accept</Button>
              )}
              {["assigned", "accepted", "paused"].includes(w.state) && (
                <Button size="sm" onClick={() => act(w.id, "/start")} data-testid={`start-${w.id}`}>
                  <PlayCircle className="w-3.5 h-3.5 mr-1"/> Start
                </Button>
              )}
              {w.state === "in_progress" && (
                <>
                  <Button size="sm" variant="outline"
                          onClick={() => { const r = prompt("Reason?"); if (r) act(w.id, "/pause", { reason: r }); }}
                          data-testid={`pause-${w.id}`}>
                    <PauseCircle className="w-3.5 h-3.5 mr-1"/> Pause
                  </Button>
                  <Button size="sm"
                          onClick={() => { const n = prompt("Resolution?"); act(w.id, "/complete", { resolution_note: n }); }}
                          data-testid={`complete-${w.id}`}>
                    <CheckCheck className="w-3.5 h-3.5 mr-1"/> Complete
                  </Button>
                </>
              )}
              {w.state === "paused" && (
                <Button size="sm" onClick={() => act(w.id, "/resume")} data-testid={`resume-${w.id}`}>Resume</Button>
              )}
              {w.state === "pending_inspection" && (
                <Badge tone="warning" size="sm">Waiting supervisor</Badge>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SlaChip({ sla }) {
  if (!sla) return <span className="text-hocp-textTertiary">—</span>;
  const diffMin = Math.round((new Date(sla).getTime() - Date.now()) / 60000);
  const overdue = diffMin < 0;
  return (
    <span className={overdue ? "text-hocp-error font-semibold" : "text-hocp-textSecondary"}>
      {overdue ? `${Math.abs(diffMin)}m late` : `${diffMin}m left`}
    </span>
  );
}
